import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ApiError } from '../../api/client'
import { importApi } from '../../api/imports'
import { mappingsApi } from '../../api/mappings'
import type { FieldDef, SavedMapping } from '../../api/types'
import { BbFieldPicker, type PickOption } from '../../components/BbFieldPicker'
import { BbIcon } from '../../components/BbIcon'
import { BbStepper } from '../../components/BbStepper'
import { IMPORT_STEPS } from './steps'
import { ErrorState, LoadingState } from '../../components/States'
import { useAsync } from '../../hooks/useAsync'
import { useImportWizard } from '../../import/ImportWizardContext'
import { useOpenSettings } from '../settings/useOpenSettings'

/* Step 3, ported from the "Map Fields" prototype.

   The grid is driven entirely by the entity definition from
   GET /api/import/entities and the headers from POST /api/uploads; the first
   pass comes from POST /api/import/suggest-mapping. Nothing here is hardcoded
   per entity.

   Import Settings modal has been removed — settings are now managed centrally at /app/settings. */

/* The server tags every field with the section of the entity's QuickBooks API
   reference it sits under ("BillAddr", "Line.SalesItemLineDetail", …; empty for
   the entity's own top level) and with that section's own title, which differs
   per entity — a bill's address is "Vendor Address", an invoice's is "Billing
   Address". Nothing about the wording is decided here; the picker groups by the
   path and prints the title the server sent. */

/** The entity's own level first, then the nested objects, then the line and its detail blocks. */
function groupRank(group: string): number {
  if (!group) return 0
  if (group === 'Line') return 2
  return group.startsWith('Line.') ? 3 : 1
}

/* What the info icon beside a field explains, beyond what the server says about
   the field itself: the kind of value QuickBooks expects there. */
const KIND_LABELS: Record<string, string> = {
  String: 'Text',
  Decimal: 'Number',
  Number: 'Number',
  Integer: 'Whole number',
  Date: 'Date',
  Boolean: 'Yes / no',
  Reference: 'Reference',
  Enum: 'Choice',
  Money: 'Money',
  Quantity: 'Quantity',
  Email: 'Email address',
  Phone: 'Phone number',
}

function describeKind(field: FieldDef): string {
  const parts: string[] = [KIND_LABELS[field.kind] ?? field.kind.toLowerCase()]
  if (field.referenceType) parts.push(`looks up a ${field.referenceType.toLowerCase()}`)
  if (field.maxLength) parts.push(`up to ${field.maxLength} characters`)
  // A line field is written on every line of the document, not once per document.
  if (field.isLineField) parts.push('set per line')
  return parts.join(' · ')
}

interface FieldGroup {
  /** The API path the section is keyed by, and the order its members are in. */
  key: string
  /** The section's title as the user reads it. */
  label: string
  /** What the section is for, when the server supplies a line about it. */
  note: string | null
  fields: FieldDef[]
}

function groupFields(fields: FieldDef[]): FieldGroup[] {
  const byGroup = new Map<string, FieldDef[]>()
  const seen = new Set<string>()
  for (const field of fields) {
    // Purchase sheets declare a field once per line family ("Expenses Class",
    // "Items Class") under one name. The mapping keys on the name, so the picker
    // offers it once, where it first appears.
    if (seen.has(field.name)) continue
    seen.add(field.name)
    byGroup.set(field.group, [...(byGroup.get(field.group) ?? []), field])
  }
  const general = [...(byGroup.get('') ?? [])]
  const groups: { key: string; fields: FieldDef[] }[] = []
  for (const [key, members] of byGroup) {
    if (!key) continue
    // A parent with a single child ("BillEmail.Address") is not worth a section.
    if (members.length < 2 && !key.startsWith('Line')) general.push(...members)
    else groups.push({ key, fields: members })
  }
  groups.sort((x, y) => groupRank(x.key) - groupRank(y.key))
  return [
    ...(general.length ? [{ ...titleOf(general), fields: general }] : []),
    ...groups.map((group) => ({ ...titleOf(group.fields), fields: group.fields })),
  ]
}

/** A section takes its title from its first field, which is where the server put it. */
function titleOf(fields: FieldDef[]): { key: string; label: string; note: string | null } {
  const first = fields[0]
  return { key: first.group, label: first.groupLabel, note: first.groupNote }
}

export function ImportMappingPage() {
  const wizard = useImportWizard()
  const navigate = useNavigate()
  const [suggesting, setSuggesting] = useState(false)
  const openSettings = useOpenSettings()
  const [error, setError] = useState<ApiError | null>(null)
  const [savedId, setSavedId] = useState('')
  const [savingMapping, setSavingMapping] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  const saved = useAsync<SavedMapping[]>(() => mappingsApi.list(), [])

  const { upload, entity, entityType, columns, setColumns, setUnmapped } = wizard
  const sheet = wizard.sheet
  const hasColumns = columns.length > 0

  // Step 3 is entered fresh from step 2, so ask the server for a first pass.
  useEffect(() => {
    if (!upload || !entityType || hasColumns) return
    // The suggest call is the side effect this step exists for; the flag has to
    // rise with it so the grid shows a spinner rather than an empty mapping.
    // oxlint-disable-next-line react/set-state-in-effect
    setSuggesting(true)
    setError(null)
    importApi
      .suggestMapping(upload.uploadId, entityType, sheet)
      .then((result) => {
        setColumns(result.columns)
        setUnmapped(result.unmapped)
      })
      .catch((cause: unknown) =>
        setError(cause instanceof ApiError ? cause : new ApiError(0, 'NETWORK', String(cause))),
      )
      .finally(() => setSuggesting(false))
  }, [upload, entityType, sheet, hasColumns, setColumns, setUnmapped])

  if (!upload || !entity) {
    return (
      <div className="bb bb-wizard">
        <div className="bb-empty-ref">
          <BbIcon name="folder" />
          <h3>Start with a file</h3>
          <p>
            Pick an import type and upload a file first.{' '}
            <Link to="/app/import">Back to step 1</Link>.
          </p>
        </div>
      </div>
    )
  }

  const byField = new Map(columns.map((column) => [column.targetField, column]))
  const byHeader = new Map<string, string>()
  for (const column of columns) {
    if (column.sourceColumn && !byHeader.has(column.sourceColumn)) {
      byHeader.set(column.sourceColumn, column.targetField)
    }
  }
  const groups = groupFields(entity.fields)
  const matched = columns.filter((column) => column.sourceColumn).length
  const missingRequired = entity.fields.filter(
    (field) => field.required && !byField.get(field.name)?.sourceColumn,
  )
  const matchedRequired = entity.fields.filter(
    (field) => field.required && byField.get(field.name)?.sourceColumn,
  )
  const entityLabel = entity.label ?? entity.name

  /* One option list, shared by every row's picker: the field's own section title
     travels with it, so does whichever file column already feeds it — what a row
     displays instead of a bare "(mapped)" tag — and so do the two strings the
     info icon shows, which the picker itself has no way to know. */
  /* One option list, shared by every row's picker. Each option carries the name
     the user reads (`displayLabel`, not the export `label`), the section it sits
     in, which file column already feeds it — what a row shows instead of a bare
     "(mapped)" tag — and the strings the info icon explains. */
  const options: PickOption[] = groups.flatMap((group) =>
    group.fields.map((field) => ({
      value: field.name,
      label: field.displayLabel,
      name: field.name,
      group: group.label,
      groupNote: group.note,
      required: field.required,
      takenBy: byField.get(field.name)?.sourceColumn ?? null,
      help: field.description,
      meta: describeKind(field),
    })),
  )

  /* The grid is driven by the file's columns, so a choice is "this column feeds
     that field". A field takes one column, so picking it moves it off whichever
     column held it; a column feeds one field here, so its earlier target is
     released. */
  const setTarget = (header: string, targetField: string | null) => {
    const released = columns.map((column) =>
      column.sourceColumn === header || (targetField && column.targetField === targetField)
        ? // A manual choice is certain, so drop the server's confidence score.
          { ...column, sourceColumn: null, confidence: 0 }
        : column,
    )
    let next = released
    if (targetField) {
      next = released.some((column) => column.targetField === targetField)
        ? released.map((column) =>
            column.targetField === targetField
              ? { ...column, sourceColumn: header, confidence: 0 }
              : column,
          )
        : [...released, { targetField, sourceColumn: header, confidence: 0 }]
    }
    setColumns(next)
    const used = new Set(next.map((column) => column.sourceColumn).filter(Boolean))
    setUnmapped(upload.headers.filter((h) => !used.has(h)))
    // The mapping changed, so the previous validation is stale.
    wizard.setValidation(null)
  }

  const sampleOf = (index: number) =>
    upload.sampleRows
      .map((row) => row[index])
      .filter((value): value is string => !!value)
      .slice(0, 2)
      .join(' · ')

  const applySaved = (id: string) => {
    setSavedId(id)
    const mapping = saved.data?.find((item) => item.id === id)
    if (!mapping) return
    /* A saved mapping was written against the field set of the day it was made,
       and the entity's set shrinks as unverified fields are removed. Keeping the
       stale columns would seed the grid and the review table with fields the
       import no longer sends, so the template is intersected with what the
       entity can carry now. What is dropped is reported rather than silent — a
       user who saved a mapping around a field needs to know it is gone. */
    const current = new Set(entity?.fields.map((field) => field.name) ?? [])
    const columns = mapping.definition.columns.filter((column) => current.has(column.targetField))
    const dropped = mapping.definition.columns.length - columns.length

    setColumns(columns)
    if (dropped > 0) {
      setNote(
        `${dropped} column${dropped === 1 ? '' : 's'} from this mapping are no longer importable and were left out.`,
      )
    }
    // The template's own options travel with it; step 4 layers the tenant's
    // current settings on top, so a stale template cannot resurrect a date
    // format the workspace has since changed.
    wizard.setOptions(mapping.definition.options ?? null)
    const used = new Set(columns.map((c) => c.sourceColumn).filter(Boolean))
    setUnmapped(upload.headers.filter((header) => !used.has(header)))
    wizard.setValidation(null)
  }

  /* Emptying the mapping is what the suggest effect waits for, so Auto-Match is
     just a re-run of the call this step already makes. */
  const autoMatch = () => {
    setNote(null)
    setSavedId('')
    setColumns([])
    wizard.setValidation(null)
  }

  /* Clearing keeps a row per field so the effect above does not immediately
     re-suggest — this is a local reset, not a request for a new guess. */
  const clearMapping = () => {
    setNote(null)
    setSavedId('')
    setColumns(
      entity.fields.map((field) => ({ targetField: field.name, sourceColumn: null, confidence: 0 })),
    )
    setUnmapped(upload.headers)
    wizard.setValidation(null)
  }

  const saveMapping = async () => {
    const name = wizard.saveMappingAs.trim()
    if (!name) return
    setSavingMapping(true)
    setNote(null)
    try {
      await mappingsApi.create(name, entityType, { entityType, columns })
      setNote(`Saved as “${name}”.`)
      saved.reload()
    } catch (cause: unknown) {
      setNote(cause instanceof ApiError ? cause.message : 'Could not save this mapping.')
    } finally {
      setSavingMapping(false)
    }
  }

  return (
    <div className="bb bb-wizard">
      <BbStepper steps={IMPORT_STEPS} current={2} />

      <h2>Map Fields</h2>
      <p className="bb-map-help">
        Match your uploaded CSV columns to QuickBooks Online {entityLabel} fields.
      </p>

      {error ? (
        <ErrorState error={error} onRetry={() => setColumns([])} />
      ) : suggesting ? (
        <LoadingState label="Matching your columns…" />
      ) : (
        <div className="bb-map">
          <div>
            <table className="bb-maptable">
              <thead>
                <tr>
                  <th className="bb-map-st">
                    <span className="sr-only">Status</span>
                  </th>
                  <th>Your File Column</th>
                  <th>QuickBooks Field</th>
                </tr>
              </thead>
              <tbody>
                {upload.headers.map((header, index) => {
                  const target = byHeader.get(header)
                  const sample = sampleOf(index)
                  return (
                    <tr key={`${index}-${header}`}>
                      <td className="bb-map-st">
                        <span className={target ? 'bb-map-ok' : 'bb-map-off'}>
                          <BbIcon name={target ? 'check-circle' : 'x-circle'} />
                          <span className="sr-only">{target ? 'Mapped' : 'Not imported'}</span>
                        </span>
                      </td>
                      <td>
                        <div className="bb-map-f">{header}</div>
                        {sample ? <div className="bb-map-help bb-map-sample">{sample}</div> : null}
                      </td>
                      <td>
                        <BbFieldPicker
                          id={`map-${index}`}
                          ariaLabel={`QuickBooks field for ${header}`}
                          value={target ?? null}
                          options={options}
                          currentColumn={header}
                          onChange={(field) => setTarget(header, field)}
                          ignoreLabel="Ignore (Do not import)"
                        />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>

            {/* The file columns the import will read, and which fields have no
                column yet. A required field is not a row of its own here — the
                grid follows the file — so this is where its absence is visible. */}
            <div className="bb-map-required">
              <div className="bb-map-required-h">
                <h3>Required QuickBooks fields</h3>
                <span className="bb-map-required-c">
                  {matchedRequired.length} of {matchedRequired.length + missingRequired.length} mapped
                </span>
              </div>

              {missingRequired.length === 0 ? (
                <p className="bb-map-required-all">
                  <BbIcon name="check-circle" />
                  Everything QuickBooks requires for {entityLabel} is mapped.
                </p>
              ) : (
                <>
                  <p className="bb-map-help">
                    These have no file column yet, so the import cannot run.
                  </p>
                  <ul className="bb-map-missing">
                    {missingRequired.map((field) => (
                      <li key={field.name}>
                        <BbIcon name="warning" />
                        <span className="bb-map-missing-f">{field.displayLabel}</span>
                        <span className="bb-pick-group">{field.groupLabel}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </div>

          <aside>
            <div className="bb-field">
              <label className="bb-aside-h" htmlFor="savedMap">
                Choose saved Mappings
              </label>
              <select
                className="bb-select"
                id="savedMap"
                value={savedId}
                onChange={(event) => applySaved(event.target.value)}
              >
                <option value="">Please select</option>
                {(saved.data ?? [])
                  .filter((mapping) => mapping.entityType === entityType)
                  .map((mapping) => (
                    <option value={mapping.id} key={mapping.id}>
                      {mapping.name}
                    </option>
                  ))}
              </select>
            </div>

            <div className="bb-field">
              <label className="bb-flabel" htmlFor="saveMappingAs">
                Name for this mapping
              </label>
              <input
                className="bb-input"
                id="saveMappingAs"
                placeholder="e.g. Monthly invoices"
                value={wizard.saveMappingAs}
                onChange={(event) => wizard.setSaveMappingAs(event.target.value)}
              />
            </div>

            <div className="bb-tilebtns">
              <button
                className="bb-tilebtn"
                type="button"
                disabled={savingMapping || !wizard.saveMappingAs.trim()}
                onClick={() => void saveMapping()}
              >
                <span className="bb-tilebtn-i">
                  <BbIcon name="save" />
                </span>
                Save Mappings
              </button>
              <button className="bb-tilebtn" type="button" onClick={autoMatch}>
                <span className="bb-tilebtn-i">
                  <BbIcon name="sync" />
                </span>
                Auto-Match
              </button>
              <button className="bb-tilebtn" type="button" onClick={clearMapping}>
                <span className="bb-tilebtn-i">
                  <BbIcon name="layers-clear" />
                </span>
                Clear Mapping
              </button>
            </div>

            <p className="bb-map-help" role="status">
              {note ??
                (missingRequired.length
                  ? `${matched} of ${entity.fields.length} fields mapped. Still missing: ${missingRequired
                      .map((field) => field.displayLabel)
                      .join(', ')}.`
                  : `${matched} of ${entity.fields.length} fields mapped.`)}
            </p>
          </aside>
        </div>
      )}

      <div className="bb-wizard-foot">
        <Link className="bb-btn bb-btn-quiet" to="/app/import/upload">
          <BbIcon name="arrow-left" />
          Previous Step
        </Link>
        <span className="bb-wizard-foot-r">
          <button className="bb-btn bb-btn-quiet" type="button" onClick={openSettings}>
            <BbIcon name="tune" />
            Settings
          </button>
          <button
            className="bb-btn bb-btn-green"
            type="button"
            disabled={missingRequired.length > 0}
            onClick={() => navigate('/app/import/review')}
          >
            Next Step
            <BbIcon name="arrow-right" />
          </button>
        </span>
      </div>

    </div>
  )
}
